import {compressMeshForMobileDevices} from "../../../../../../../helpers/sqft3dArSceneHelper";
import {
  isRuntimeCabinetHandle,
  markRuntimeCabinetHandle,
  removeRuntimeCabinetHandles,
} from './cabinetHandleRuntime';

const DRAWER_HANDLE_PLACEMENT_NAME = /^Drawer_Handle(?:\d*|\.\d+)?$/;
const DOOR_HANDLE_PLACEMENT_NAME = /^Door_Handle(?:\d*|\.\d+)?$/;
// Old cabinets author the drawer front as "Drawer_<digits>" (e.g. Drawer_018113).
// This must be distinguished from generic "Drawer_Base"/"Drawer_Cap" component
// names, which are never valid handle targets.
const LEGACY_DRAWER_FRONT_NAME = /^Drawer_\d+(?:_clone_\d+)?$/;
const EDITABLE_DRAWER_FRONT_NAME = /^Drawer(?:\d*|\.\d+)?(?:_clone_\d+)?$/;
const EDITABLE_DOOR_FRONT_NAME = /^Door[ _](?:Left|Right)(?:\d*|\.\d+)?(?:_clone_\d+)?$/;

class HandlesLoader {
  constructor(scene, loader) {
    this.scene = scene;
    this.loader = loader;
    this.handleCache = new Map();
  }

  getHandleTargetObjects(cabinetScene) {
    const drawerHandlePlacements = [];
    const doorHandlePlacements = [];
    const legacyDrawerFronts = [];
    const editableDrawerFronts = [];
    const editableDoorFronts = [];
    const legacyDrawerObjects = [];
    const legacyDoorObjects = [];

    cabinetScene.traverse((object) => {
      if (!object.name || isRuntimeCabinetHandle(object)) {
        return;
      }

      if (DRAWER_HANDLE_PLACEMENT_NAME.test(object.name)) {
        drawerHandlePlacements.push(object);
      } else if (LEGACY_DRAWER_FRONT_NAME.test(object.name)) {
        legacyDrawerFronts.push(object);
      } else if (EDITABLE_DRAWER_FRONT_NAME.test(object.name)) {
        editableDrawerFronts.push(object);
      } else if (object.name.includes("Drawer_")) {
        legacyDrawerObjects.push(object);
      }

      if (DOOR_HANDLE_PLACEMENT_NAME.test(object.name)) {
        doorHandlePlacements.push(object);
      } else if (EDITABLE_DOOR_FRONT_NAME.test(object.name)) {
        editableDoorFronts.push(object);
      } else if (object.name.includes("Door_")) {
        legacyDoorObjects.push(object);
      }
    });

    return {
      drawerObjects: drawerHandlePlacements.length
        ? drawerHandlePlacements
        : (legacyDrawerFronts.length
          ? legacyDrawerFronts
          : (editableDrawerFronts.length ? editableDrawerFronts : legacyDrawerObjects)),
      doorObjects: doorHandlePlacements.length
        ? doorHandlePlacements
        : (editableDoorFronts.length ? editableDoorFronts : legacyDoorObjects),
    };
  }

  async loadHandles(cabinetData, cabinetScene, shouldContinue = () => true) {
    const {drawerObjects, doorObjects} = this.getHandleTargetObjects(cabinetScene);

    if (drawerObjects.length === 0 && doorObjects.length === 0) {
      this.logMeshes();
      console.log(`Handles position not found in the scene`);
      return;
    }

    for (const handleIndex in cabinetData.handles) {
      if (!cabinetData.handles.hasOwnProperty(handleIndex)) continue;

      const handle = cabinetData.handles[handleIndex];

      // DRAWERS will use the same vertical handles model but rotated
      if (handle.handleH3dURL && handle.quantityGroup === "drawers" && drawerObjects.length > 0) {
        await this.loadAndApplyHandle(handle.handleH3dURL, drawerObjects, "H", shouldContinue);
      }

      // Doors
      if (handle.handleV3dURL && handle.quantityGroup === "doors" && doorObjects.length > 0) {
        await this.loadAndApplyHandle(handle.handleV3dURL, doorObjects, "V", shouldContinue);
      }
    }
  }

  async replaceHandles(cabinetData, cabinetScene) {
    const loadVersion = (cabinetScene.userData.cabinetHandleLoadVersion || 0) + 1;
    cabinetScene.userData.cabinetHandleLoadVersion = loadVersion;
    removeRuntimeCabinetHandles(cabinetScene);

    const shouldContinue = () => cabinetScene.userData.cabinetHandleLoadVersion === loadVersion;
    await this.loadHandles(cabinetData, cabinetScene, shouldContinue);
  }

  async loadAndApplyHandle(handleURL, targetObjects, orientationTag, shouldContinue = () => true) {
    const cacheKey = `${handleURL}::${orientationTag}`;

    if (this.handleCache.has(cacheKey)) {
      const cachedHandle = this.handleCache.get(cacheKey);
      if (shouldContinue()) {
        this.applyHandleToObjects(cachedHandle, targetObjects);
      }
    } else {
      const gltfHandle = await this.loadHandle(handleURL, orientationTag);
      if (gltfHandle && shouldContinue()) {
        this.handleCache.set(cacheKey, gltfHandle);
        this.applyHandleToObjects(gltfHandle, targetObjects);
      }
    }
  }

  async loadHandle(handle3dURL, orientationTag) {
    return new Promise((resolve, reject) => {
      this.loader.load(handle3dURL, (gltfHandle) => {
        if (!gltfHandle || !gltfHandle.scene) {
          console.error(`GLTF scene not found for URL ${handle3dURL}`);
          reject(new Error("GLTF scene not found"));
          return;
        }

        let handleMesh = null;
        gltfHandle.scene.traverse((child) => {
          if (child.isMesh && child.name.includes(`_${orientationTag}_`)) {
            handleMesh = child.clone();
            compressMeshForMobileDevices(handleMesh);
          }
        });

        if (handleMesh) {
          resolve(handleMesh);
        } else {
          reject(new Error(`No matching mesh found with tag _${orientationTag}_ in GLTF`));
        }
      }, undefined, (error) => {
        console.error(`Error loading handle from URL ${handle3dURL}:`, error);
        reject(error);
      });
    });
  }


  applyHandleToObjects(handleMesh, targetObjects, rotate) {
    for (const obj of targetObjects) {
      this.applyHandleAsChild(handleMesh, obj, rotate);
    }
  }

  applyHandleAsChild(handleMesh, objectPlacement, rotate = false) {
    const handleClone = handleMesh.clone();
    markRuntimeCabinetHandle(handleClone);

    if (rotate) {
      handleClone.rotation.z += Math.PI / 2; // Rotate 90 degrees
    }

    objectPlacement.add(handleClone);
  }

  logMeshes() {
    const allAssets = [];
    this.scene.traverse((object) => {
      allAssets.push(object);
    });

    console.log(allAssets);
  }
}

export default HandlesLoader;
